import React from 'react';
import { Link } from 'react-router-dom';
import type { SignupCredentials } from '../types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { apiService } from '../services/ApiService';

interface SignupPageProps {
  onSignup: (credentials: SignupCredentials) => void;
  loading?: boolean;
}

export const SignupPage: React.FC<SignupPageProps> = ({ onSignup, loading = false }) => {
  const [fullName, setFullName] = React.useState('');
  const [username, setUsername] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [confirmPassword, setConfirmPassword] = React.useState('');
  const [usernameAvailable, setUsernameAvailable] = React.useState<boolean | null>(null);
  const [checkingUsername, setCheckingUsername] = React.useState(false);
  const [usernameError, setUsernameError] = React.useState('');

  // Debounced username availability check
  React.useEffect(() => {
    if (username.length < 3) {
      setUsernameAvailable(null);
      setUsernameError('');
      return;
    }

    const usernameRegex = /^[a-zA-Z0-9_-]+$/;
    if (!usernameRegex.test(username)) {
      setUsernameAvailable(false);
      setUsernameError('Username can only contain letters, numbers, underscores, and hyphens');
      return;
    }

    const timeoutId = setTimeout(async () => {
      setCheckingUsername(true);
      setUsernameError('');
      try {
        const result = await apiService.checkUsername(username);
        setUsernameAvailable(result.available);
        if (!result.available) {
          setUsernameError('Username is already taken');
        }
      } catch (error) {
        console.error('Username check error:', error);
        setUsernameError('Error checking username availability');
        setUsernameAvailable(null);
      } finally {
        setCheckingUsername(false);
      }
    }, 1000); // Increased debounce to 1 second

    return () => clearTimeout(timeoutId);
  }, [username]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (fullName && username && email && password && password === confirmPassword && usernameAvailable) {
      onSignup({ fullName, username, email, password });
    }
  };

  const passwordsMatch = password === confirmPassword;
  const isFormValid = fullName && username && email && password && passwordsMatch && usernameAvailable;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50/50 px-4">
      <Card className="w-full max-w-md border-0 shadow-sm">
        <CardHeader className="space-y-2 text-center pb-8">
          <CardTitle className="text-2xl font-normal text-gray-900">
            Create your account
          </CardTitle>
          <CardDescription className="text-gray-600">
            Enter your details to get started with OnePager Hub
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fullName" className="text-sm font-normal text-gray-700">
                Full Name
              </Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                disabled={loading}
                className="h-11 border-gray-200 focus:border-gray-900 focus:ring-0"
                placeholder="Enter your full name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="username" className="text-sm font-normal text-gray-700">
                Username
              </Label>
              <div className="relative">
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.toLowerCase())}
                  required
                  disabled={loading}
                  className={`h-11 border-gray-200 focus:border-gray-900 focus:ring-0 pr-10 ${
                    username.length >= 3 && usernameAvailable === false ? 'border-red-300' : ''
                  } ${
                    username.length >= 3 && usernameAvailable === true ? 'border-green-300' : ''
                  }`}
                  placeholder="Choose a username"
                />
                {checkingUsername && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                  </div>
                )}
                {!checkingUsername && username.length >= 3 && usernameAvailable === true && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg className="h-4 w-4 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                {!checkingUsername && username.length >= 3 && usernameAvailable === false && (
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                    <svg className="h-4 w-4 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                )}
              </div>
              {usernameError && (
                <p className="text-sm text-red-600">{usernameError}</p>
              )}
              {username.length > 0 && username.length < 3 && (
                <p className="text-sm text-gray-500">Username must be at least 3 characters long</p>
              )}
              {!checkingUsername && usernameAvailable === true && (
                <p className="text-sm text-green-600">Username is available!</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-normal text-gray-700">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
                className="h-11 border-gray-200 focus:border-gray-900 focus:ring-0"
                placeholder="Enter your email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-normal text-gray-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                className="h-11 border-gray-200 focus:border-gray-900 focus:ring-0"
                placeholder="Create a password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm font-normal text-gray-700">
                Confirm password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
                className="h-11 border-gray-200 focus:border-gray-900 focus:ring-0"
                placeholder="Confirm your password"
              />
              {confirmPassword && !passwordsMatch && (
                <p className="text-sm text-red-600">Passwords do not match</p>
              )}
            </div>
            <Button 
              type="submit" 
              disabled={loading || !isFormValid}
              className="w-full h-11 bg-gray-900 hover:bg-gray-800 text-white font-normal disabled:opacity-50"
            >
              {loading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>
          <div className="text-center">
            <span className="text-sm text-gray-600">
              Already have an account?{' '}
              <Link to="/login" className="text-gray-900 hover:underline font-medium">
                Sign in
              </Link>
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};